<template>
  <div class="modal fade show d-block" tabindex="-1" style="background-color: rgba(0,0,0,0.5)">
    <div class="modal-dialog modal-lg modal-dialog-centered">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title">Create Patient Referral</h5>
          <button type="button" class="btn-close" @click="$emit('close')"></button>
        </div>
        <div class="modal-body">
          <!-- Step 1: Select Patient -->
          <div v-if="step === 1">
            <div class="mb-4">
              <h6 class="mb-3">Select Patient to Refer</h6>

              <!-- Search -->
              <div class="search-box mb-3">
                <div class="position-relative">
                  <input v-model="searchQuery" @input="searchMembers" type="text"
                         class="form-control" placeholder="Search by name, CBHI number, or phone...">
                  <i class="bx bx-search-alt search-icon"></i>
                </div>
              </div>

              <!-- Recent Patients -->
              <div v-if="recentPatients.length > 0 && !searchQuery" class="mb-4">
                <h6 class="text-muted mb-2">Recent Patients</h6>
                <div class="list-group">
                  <div v-for="patient in recentPatients" :key="patient.member_id"
                       class="list-group-item list-group-item-action"
                       :class="{'active': selectedPatient?.member_id === patient.member_id}"
                       @click="selectPatient(patient)">
                    <div class="d-flex justify-content-between align-items-center">
                      <div>
                        <h6 class="mb-1">{{ patient.first_name }} {{ patient.surname }}</h6>
                        <p class="mb-0 text-muted">
                          {{ getPatientInfo(patient) }}
                        </p>
                      </div>
                      <div>
                        <span v-if="patient.enrollment_type === 'cbhi_member'"
                              class="badge bg-success">CBHI</span>
                        <span v-if="patient.is_pregnant" class="badge bg-warning ms-1">
                          <i class="bx bx-female"></i> Pregnant
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Search Results -->
              <div v-if="searchResults.length > 0">
                <h6 class="text-muted mb-2">Search Results</h6>
                <div class="list-group">
                  <div v-for="patient in searchResults" :key="patient.member_id"
                       class="list-group-item list-group-item-action"
                       :class="{'active': selectedPatient?.member_id === patient.member_id}"
                       @click="selectPatient(patient)">
                    <div class="d-flex justify-content-between align-items-center">
                      <div>
                        <h6 class="mb-1">{{ patient.first_name }} {{ patient.surname }}</h6>
                        <p class="mb-0 text-muted">
                          {{ getPatientInfo(patient) }}
                        </p>
                      </div>
                      <div>
                        <span v-if="patient.enrollment_type === 'cbhi_member'"
                              class="badge bg-success">CBHI</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- No Results -->
              <div v-else-if="searchQuery && !loading" class="text-center py-4">
                <p class="text-muted">No patients found</p>
              </div>
            </div>
          </div>

          <!-- Step 2: Referral Type & Reason -->
          <div v-else-if="step === 2">
            <div class="mb-4">
              <h6 class="mb-3">Referral Details</h6>

              <!-- Patient Info -->
              <div class="alert alert-info">
                <div class="d-flex justify-content-between">
                  <div>
                    <h6 class="alert-heading">{{ selectedPatient.first_name }} {{ selectedPatient.surname }}</h6>
                    <p class="mb-0">
                      {{ getPatientInfo(selectedPatient) }}<br>
                      CBHI: {{ selectedPatient.cbhi_number || 'Not enrolled' }}
                    </p>
                  </div>
                  <div class="text-end">
                    <button @click="previousStep" class="btn btn-sm btn-secondary">
                      <i class="bx bx-arrow-back"></i> Change Patient
                    </button>
                  </div>
                </div>
              </div>

              <!-- Urgency Level -->
              <div class="mb-3">
                <label class="form-label">Urgency Level <span class="text-danger">*</span></label>
                <div class="row g-2">
                  <div class="col-md-4">
                    <div class="form-check card p-3"
                         :class="{'border-danger border-2': referralForm.urgency === 'emergency'}">
                      <input class="form-check-input" type="radio" name="urgency"
                             id="urgencyEmergency" value="emergency" v-model="referralForm.urgency">
                      <label class="form-check-label ms-2" for="urgencyEmergency">
                        <strong class="text-danger">Emergency</strong>
                        <br><small>Immediate attention required</small>
                      </label>
                    </div>
                  </div>
                  <div class="col-md-4">
                    <div class="form-check card p-3"
                         :class="{'border-warning border-2': referralForm.urgency === 'urgent'}">
                      <input class="form-check-input" type="radio" name="urgency"
                             id="urgencyUrgent" value="urgent" v-model="referralForm.urgency">
                      <label class="form-check-label ms-2" for="urgencyUrgent">
                        <strong class="text-warning">Urgent</strong>
                        <br><small>Within 24 hours</small>
                      </label>
                    </div>
                  </div>
                  <div class="col-md-4">
                    <div class="form-check card p-3"
                         :class="{'border-primary border-2': referralForm.urgency === 'routine'}">
                      <input class="form-check-input" type="radio" name="urgency"
                             id="urgencyRoutine" value="routine" v-model="referralForm.urgency">
                      <label class="form-check-label ms-2" for="urgencyRoutine">
                        <strong class="text-primary">Routine</strong>
                        <br><small>Scheduled appointment</small>
                      </label>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Referral Type -->
              <div class="row">
                <div class="col-md-6 mb-3">
                  <label class="form-label">Referral Type <span class="text-danger">*</span></label>
                  <select v-model="referralForm.referralType" class="form-select" required>
                    <option value="">Select referral type</option>
                    <option value="maternal_health">Maternal Health</option>
                    <option value="child_health">Child Health</option>
                    <option value="chronic_disease">Chronic Disease</option>
                    <option value="emergency_care">Emergency Care</option>
                    <option value="specialized_care">Specialized Care</option>
                    <option value="diagnostic">Diagnostic Services</option>
                    <option value="surgical">Surgical</option>
                    <option value="mental_health">Mental Health</option>
                    <option value="other">Other</option>
                  </select>
                </div>

                <div class="col-md-6 mb-3">
                  <label class="form-label">Refer To <span class="text-danger">*</span></label>
                  <select v-model="referralForm.referralTo" class="form-select"
                          @change="onFacilityChange" required>
                    <option value="">Select facility</option>
                    <optgroup label="Health Centers">
                      <option v-for="facility in healthCenters" :key="facility.facility_id"
                              :value="facility.facility_id">
                        {{ facility.facility_name }} - {{ facility.woreda_name }}
                      </option>
                    </optgroup>
                    <optgroup label="Hospitals">
                      <option v-for="facility in hospitals" :key="facility.facility_id"
                              :value="facility.facility_id">
                        {{ facility.facility_name }} - {{ facility.woreda_name }}
                      </option>
                    </optgroup>
                  </select>
                </div>
              </div>

              <!-- Referral Reason -->
              <div class="mb-3">
                <label class="form-label">Reason for Referral <span class="text-danger">*</span></label>
                <textarea v-model="referralForm.referralReason" class="form-control" rows="3"
                          placeholder="Describe the reason for referral..." required></textarea>
              </div>

              <!-- Clinical Notes -->
              <div class="mb-3">
                <label class="form-label">Clinical Notes / Current Condition</label>
                <textarea v-model="referralForm.clinicalNotes" class="form-control" rows="3"
                          placeholder="Vital signs, symptoms, current medications..."></textarea>
              </div>
            </div>
          </div>

          <!-- Step 3: Transport & Follow-up -->
          <div v-else-if="step === 3">
            <div class="mb-4">
              <h6 class="mb-3">Transport & Follow-up Arrangements</h6>

              <!-- Referral Summary -->
              <div class="alert"
                   :class="{'alert-danger': referralForm.urgency === 'emergency',
                            'alert-warning': referralForm.urgency === 'urgent',
                            'alert-info': referralForm.urgency === 'routine'}">
                <h6 class="alert-heading">
                  {{ referralForm.urgency.toUpperCase() }} Referral
                </h6>
                <p class="mb-1">
                  <strong>Patient:</strong> {{ selectedPatient.first_name }} {{ selectedPatient.surname }}<br>
                  <strong>To:</strong> {{ getSelectedFacilityName() }}<br>
                  <strong>Type:</strong> {{ getReferralTypeLabel() }}
                </p>
              </div>

              <!-- Transport Arrangement -->
              <div class="mb-4">
                <div class="form-check mb-3">
                  <input v-model="referralForm.transportArranged" class="form-check-input"
                         type="checkbox" id="transportCheck">
                  <label class="form-check-label" for="transportCheck">
                    Transport has been arranged
                  </label>
                </div>

                <div v-if="referralForm.transportArranged" class="ms-4">
                  <div class="row">
                    <div class="col-md-6 mb-3">
                      <label class="form-label">Transport Type</label>
                      <select v-model="referralForm.transportType" class="form-select">
                        <option value="">Select transport type</option>
                        <option value="ambulance">Ambulance</option>
                        <option value="health_center_vehicle">Health Center Vehicle</option>
                        <option value="private_vehicle">Private Vehicle</option>
                        <option value="public_transport">Public Transport</option>
                        <option value="motorcycle">Motorcycle</option>
                        <option value="walking">Walking</option>
                      </select>
                    </div>

                    <div class="col-md-6 mb-3">
                      <label class="form-label">Accompanied By</label>
                      <input v-model="referralForm.accompaniedBy" type="text" class="form-control"
                             placeholder="Name of person accompanying patient">
                    </div>
                  </div>
                </div>
              </div>

              <!-- Emergency Contact -->
              <div v-if="referralForm.urgency !== 'routine'" class="mb-3">
                <label class="form-label">Emergency Contact Number</label>
                <input v-model="referralForm.emergencyContact" type="tel" class="form-control"
                       placeholder="Phone number for emergency contact">
              </div>

              <!-- Print Options -->
              <div class="mb-3">
                <div class="form-check">
                  <input v-model="printReferralSlip" class="form-check-input"
                         type="checkbox" id="printCheck" checked>
                  <label class="form-check-label" for="printCheck">
                    Print referral slip after creating
                  </label>
                </div>
              </div>

              <!-- Instructions -->
              <div class="alert alert-light">
                <h6 class="alert-heading">
                  <i class="bx bx-info-circle"></i> Important Instructions
                </h6>
                <ul class="mb-0">
                  <li>Ensure patient/guardian understands the referral reason and urgency</li>
                  <li>Provide the referral slip to the patient/guardian</li>
                  <li>For emergency cases, call the receiving facility to alert them</li>
                  <li>Follow up within 48 hours to confirm patient reached the facility</li>
                </ul>
              </div>
            </div>
          </div>

          <!-- Loading State -->
          <div v-if="loading" class="text-center py-4">
            <div class="spinner-border text-primary" role="status">
              <span class="visually-hidden">Loading...</span>
            </div>
          </div>
        </div>

        <div class="modal-footer">
          <button type="button" class="btn btn-secondary" @click="$emit('close')">Cancel</button>

          <button v-if="step === 1" type="button" class="btn btn-primary"
                  @click="nextStep" :disabled="!selectedPatient">
            Next <i class="bx bx-chevron-right"></i>
          </button>

          <button v-else-if="step === 2" type="button" class="btn btn-primary"
                  @click="nextStep" :disabled="!isStep2Valid()">
            Next <i class="bx bx-chevron-right"></i>
          </button>

          <div v-else-if="step === 3">
            <button type="button" class="btn btn-secondary me-2" @click="previousStep">
              <i class="bx bx-chevron-left"></i> Back
            </button>
            <button type="button" class="btn btn-danger" @click="createReferral"
                    :disabled="submitting">
              <span v-if="submitting">
                <span class="spinner-border spinner-border-sm me-1"></span> Creating...
              </span>
              <span v-else>
                <i class="bx bx-check"></i> Create Referral
              </span>
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
/* eslint-disable */
import { authUrl, executeJson } from "@/api";

export default {
  name: "ReferralModal",
  data() {
    return {
      step: 1,
      loading: false,
      submitting: false,

      // Patient selection
      searchQuery: '',
      searchResults: [],
      recentPatients: [],
      selectedPatient: null,

      // Facilities
      healthCenters: [],
      hospitals: [],

      // Referral form
      referralForm: {
        urgency: 'routine',
        referralType: '',
        referralTo: '',
        referralReason: '',
        clinicalNotes: '',
        transportArranged: false,
        transportType: '',
        accompaniedBy: '',
        emergencyContact: ''
      },

      printReferralSlip: true
    };
  },

  mounted() {
    this.loadRecentPatients();
    this.loadFacilities();
  },

  methods: {
    async loadRecentPatients() {
      // Load patients recently seen by this HEW
      const data = {
        "function": "getRecentPatients",
        "limit": 5
      };

      try {
        const res = await executeJson(data, authUrl);
        if (res.data.success) {
          this.recentPatients = res.data.data.patients || [];
        }
      } catch (e) {
        console.error("Failed to load recent patients:", e);
      }
    },

    async loadFacilities() {
      const data = {
        "function": "getReferralFacilities"
      };

      try {
        const res = await executeJson(data, authUrl);
        if (res.data.success) {
          this.healthCenters = res.data.data.health_centers || [];
          this.hospitals = res.data.data.hospitals || [];
        }
      } catch (e) {
        console.error("Failed to load facilities:", e);
      }
    },

    async searchMembers() {
      if (!this.searchQuery || this.searchQuery.length < 2) {
        this.searchResults = [];
        return;
      }

      this.loading = true;

      const data = {
        "function": "searchMembers",
        "searchTerm": this.searchQuery,
        "searchType": "all"
      };

      try {
        const res = await executeJson(data, authUrl);

        if (res.data.success) {
          this.searchResults = res.data.data.members || [];
        }
      } catch (e) {
        console.error("Failed to search members:", e);
      } finally {
        this.loading = false;
      }
    },

    selectPatient(patient) {
      this.selectedPatient = patient;
    },

    nextStep() {
      if (this.step === 1 && this.selectedPatient) {
        this.step = 2;
      } else if (this.step === 2 && this.isStep2Valid()) {
        this.step = 3;
      }
    },

    previousStep() {
      if (this.step > 1) {
        this.step--;
      }
    },

    isStep2Valid() {
      return this.referralForm.urgency &&
          this.referralForm.referralType &&
          this.referralForm.referralTo &&
          this.referralForm.referralReason;
    },

    onFacilityChange() {
      // Could load facility-specific information here
    },

    getSelectedFacilityName() {
      const allFacilities = [...this.healthCenters, ...this.hospitals];
      const facility = allFacilities.find(f => f.facility_id == this.referralForm.referralTo);
      return facility ? facility.facility_name : '';
    },

    getReferralTypeLabel() {
      const types = {
        'maternal_health': 'Maternal Health',
        'child_health': 'Child Health',
        'chronic_disease': 'Chronic Disease',
        'emergency_care': 'Emergency Care',
        'specialized_care': 'Specialized Care',
        'diagnostic': 'Diagnostic Services',
        'surgical': 'Surgical',
        'mental_health': 'Mental Health',
        'other': 'Other'
      };
      return types[this.referralForm.referralType] || this.referralForm.referralType;
    },

    async createReferral() {
      this.submitting = true;

      const data = {
        "function": "createReferral",
        "memberId": this.selectedPatient.member_id,
        "referralType": this.referralForm.referralType,
        "referralReason": this.referralForm.referralReason,
        "referralTo": this.getSelectedFacilityName(),
        "referralToFacilityId": this.referralForm.referralTo,
        "urgency": this.referralForm.urgency,
        "clinicalNotes": this.referralForm.clinicalNotes,
        "transportArranged": this.referralForm.transportArranged,
        "transportType": this.referralForm.transportType,
        "accompaniedBy": this.referralForm.accompaniedBy
      };

      try {
        const res = await executeJson(data, authUrl);

        if (res.data.success) {
          // Print referral slip if requested
          if (this.printReferralSlip) {
            this.printReferral(res.data.data);
          }

          this.$emit('created', res.data.data);
        } else {
          alert(res.data.message || 'Failed to create referral');
        }
      } catch (e) {
        console.error("Failed to create referral:", e);
        alert('Failed to create referral. Please try again.');
      } finally {
        this.submitting = false;
      }
    },

    printReferral(referralData) {
      // Open print dialog for referral slip
      // This would typically open a new window with the referral details formatted for printing
      console.log('Printing referral:', referralData);
      // window.open(`/print/referral/${referralData.referralId}`, '_blank');
    },

    getPatientInfo(patient) {
      const parts = [];

      if (patient.age_years !== undefined) {
        parts.push(`Age: ${patient.age_years} years`);
      } else if (patient.date_of_birth) {
        const age = this.calculateAge(patient.date_of_birth);
        parts.push(`Age: ${age}`);
      }

      if (patient.gender) {
        parts.push(patient.gender);
      }

      if (patient.household_number) {
        parts.push(`HH: ${patient.household_number}`);
      }

      return parts.join(' • ');
    },

    calculateAge(dateOfBirth) {
      const today = new Date();
      const birthDate = new Date(dateOfBirth);
      const age = today.getFullYear() - birthDate.getFullYear();
      const monthDiff = today.getMonth() - birthDate.getMonth();

      if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
        return age - 1 + ' years';
      }

      return age + ' years';
    }
  }
};
</script>

<style scoped>
.modal {
  display: block;
}

.search-icon {
  position: absolute;
  top: 50%;
  right: 10px;
  transform: translateY(-50%);
  color: #999;
}

.list-group-item {
  cursor: pointer;
}

.list-group-item:hover {
  background-color: #f8f9fa;
}

.list-group-item.active {
  background-color: #556ee6;
  border-color: #556ee6;
}

.spinner-border-sm {
  width: 1rem;
  height: 1rem;
  border-width: 0.15em;
}

.form-check.card {
  cursor: pointer;
  transition: all 0.2s;
}

.form-check.card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0,0,0,0.1);
}

.border-2 {
  border-width: 2px !important;
}
</style>